"use client";

import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  canUseMerchantPermission,
  shopInventoryApi,
  type ShopInventoryLotWithSerialsLookupItem,
  type ShopInventoryStockRow,
} from "@/modules/merchant/presentation/api/client";
import {
  useShopInventoryBranchSkuStock,
  useShopInventoryLotLabelsQuery,
  useShopInventoryMovementsQuery,
  useShopInventoryReconcileQuery,
  useShopInventorySerialsQuery,
} from "@/modules/merchant/presentation/api/queries";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { ShopInventoryLocationDistributionPanel } from "@/modules/merchant/presentation/ui/ShopInventoryLocationDistributionPanel";
import { hasShopFeature } from "@/modules/merchant/presentation/lib/has-shop-feature";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { movementTypeBadge } from "@/modules/merchant/presentation/ui/ShopInventoryMovementsFilter";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SummaryCard } from "@/shared/ui/SummaryCard";
import { Badge, Button, cn, Loader } from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowDownToLine,
  CheckCircle2,
  History,
  ImageIcon,
  Lock,
  PackageIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

const INVENTORY_ROUTE = "/app/shops/$shopId/inventory" as const;
const INVENTORY_RECEIVE_ROUTE = "/app/shops/$shopId/inventory/receive" as const;
const INVENTORY_MOVEMENTS_ROUTE =
  "/app/shops/$shopId/inventory/movements" as const;

const SKU_DETAIL_TABLE_PAGE = 20;

type SkuDetailSearch = { branchId?: string };

function InventorySubtablePager({
  offset,
  limit,
  total,
  numberFmt,
  onPrev,
  onNext,
  disabled,
}: {
  offset: number;
  limit: number;
  total: number;
  numberFmt: Intl.NumberFormat;
  onPrev: () => void;
  onNext: () => void;
  disabled?: boolean;
}) {
  if (total === 0 && offset === 0) return null;
  const pageEnd = Math.min(offset + limit, total);
  const hasPrev = offset > 0;
  const hasNext = offset + limit < total;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-border border-t bg-muted/20 px-3 py-2 text-muted-foreground text-sm">
      <span>
        {total === 0
          ? "0"
          : `${offset + 1}–${pageEnd} ຈາກ ${numberFmt.format(total)}`}
      </span>
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || !hasPrev}
          onClick={onPrev}
        >
          ກ່ອນໜ້າ
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || !hasNext}
          onClick={onNext}
        >
          ຖັດໄປ
        </Button>
      </div>
    </div>
  );
}

function trackingDisplayLabel(trackingMode: string): string {
  if (trackingMode === "lot_serial" || trackingMode === "serial") {
    return "Lot + Serial";
  }
  if (trackingMode === "lot") return "Lot";
  return "ບໍ່ຕິດຕາມ lot/serial";
}

function skuUsesLotTable(tm: string): boolean {
  return tm === "lot" || tm === "lot_serial";
}

function skuUsesSerialTable(tm: string): boolean {
  return tm === "lot_serial" || tm === "serial";
}

/** ຄຳອະທິບາຍກາດສະຫຼຸບຍອດ — ຕ່າງກັນຕາມຮູບແບບຕິດຕາມ lot/serial */
function stockSummaryDescriptions(trackingMode: string): {
  onHand: string;
  reserved: string;
  available: string;
} {
  const reservedCommon = "ຖືກຈອງໄວ້ສຳລັບອໍເດີ ຫຼືການຂາຍທີ່ຍັງບໍ່ສຳເລັດ";

  if (trackingMode === "lot") {
    return {
      onHand: "ປະລິມານລວມໃນສາຂາ — ກວດຄືນໄດ້ຈາກຜົນລວມຈຳນວນຕໍ່ lot",
      reserved: reservedCommon,
      available: "ຄົງເຫຼືອຫັກຈອງ — ຂາຍໄດ້ໂດຍຕັດຈາກ lot ທີ່ມີຍອດ",
    };
  }

  if (trackingMode === "lot_serial") {
    return {
      onHand: "ຍອດລວມຄວນສອດຄ່ອງກັບ serial ຄ້າງສະຕ໋ອກ ແລະ ປະຫວັດຕາມ lot",
      reserved: "ອາດຈອງຕາມ serial/ຫນ່ວຍ ຫຼືການຂາຍຄ້າງ — ຫັກຈາກຍອດທີ່ຕິດຕາມແລ້ວ",
      available: "ຫຼັງຫັກຈອງ — ພ້ອມຂາຍເມື່ອມີ serial ຢູ່ສະຕ໋ອກ",
    };
  }

  if (trackingMode === "serial") {
    return {
      onHand: "ນັບຕາມຫນ່ວຍ serial ທີ່ຢູ່ສະຕ໋ອກ (ຕິດຕາມເປັນລາຍຕົວ)",
      reserved: reservedCommon,
      available: "ຄົງເຫຼືອຫັກຈອງ — ຂາຍໄດ້ເປັນລາຍຕົວ serial",
    };
  }

  return {
    onHand: "ຈຳນວນລວມໃນສາຂານີ້ ຕາມລະບົບສະຕ໋ອກ (ບໍ່ແຍກ lot/serial)",
    reserved: reservedCommon,
    available: "ຄົງເຫຼືອຫັກຈອງ — ພ້ອມຂາຍ ຫຼື ນຳໃຊ້ໄດ້",
  };
}

function reconcileMismatchLabels(trackingMode: string): {
  title: string;
  derivedLabel: string;
} {
  if (trackingMode === "lot") {
    return {
      title: "ຍອດລວມບໍ່ກົງກັບຜົນລວມ lot",
      derivedLabel: "ຄິດຈາກ lot",
    };
  }
  if (trackingMode === "lot_serial" || trackingMode === "serial") {
    return {
      title: "ຍອດລວມບໍ່ກົງກັບ lot ແລະ serial",
      derivedLabel: "ຄິດຈາກ lot/serial",
    };
  }
  return {
    title: "ຍອດລວມບໍ່ກົງກັບການຄິດໄລ່",
    derivedLabel: "ຄ່າອ້າງອີງ",
  };
}

export function ShopInventorySkuDetailPage() {
  const params = useParams({ strict: false });
  const search = (useSearch({ strict: false }) ?? {}) as SkuDetailSearch;
  const nav = useNavigate();

  const shopId = params?.shopId as string | undefined;
  const shopProductSkuId = params?.shopProductSkuId as string | undefined;
  const branchId =
    typeof search.branchId === "string" && search.branchId.trim() !== ""
      ? search.branchId.trim()
      : undefined;

  const { currentPermissions, currentShop } = useMerchantShops(shopId);
  const verifiedLevel = currentShop?.verifiedLevel ?? null;
  const canReceive = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.inventory.receive,
    verifiedLevel,
  );
  const canAdjust = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.inventory.adjust,
    verifiedLevel,
  );
  const warehouseLocationsOn = hasShopFeature(
    currentShop?.featureFlags,
    "warehouse_locations",
  );

  const stockQ = useShopInventoryBranchSkuStock(shopId, {
    branchId,
    shopProductSkuId,
    enabled: !!shopId && !!branchId && !!shopProductSkuId,
  });

  const row: ShopInventoryStockRow | null = stockQ.data?.data?.[0] ?? null;
  const tm = row?.trackingMode ?? "";
  const stockSumDesc = useMemo(() => stockSummaryDescriptions(tm), [tm]);
  const reconcileLabels = useMemo(() => reconcileMismatchLabels(tm), [tm]);

  const [lotsOffset, setLotsOffset] = useState(0);
  const [serialsOffset, setSerialsOffset] = useState(0);
  const [serialLotId, setSerialLotId] = useState("");
  const [movementsOffset, setMovementsOffset] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset pagination when branch/SKU changes
  useEffect(() => {
    setLotsOffset(0);
    setSerialsOffset(0);
    setSerialLotId("");
    setMovementsOffset(0);
  }, [branchId, shopProductSkuId]);

  const lotsQ = useShopInventoryLotLabelsQuery(shopId, {
    branchId,
    shopProductSkuId,
    limit: SKU_DETAIL_TABLE_PAGE,
    offset: lotsOffset,
    enabled: !!row && skuUsesLotTable(tm),
  });

  const serialLotFilter =
    serialLotId.trim() !== "" ? serialLotId.trim() : undefined;

  const serialsQ = useShopInventorySerialsQuery(shopId, {
    branchId,
    shopProductSkuId,
    limit: SKU_DETAIL_TABLE_PAGE,
    offset: serialsOffset,
    lotId: serialLotFilter,
    enabled: !!row && skuUsesSerialTable(tm),
  });

  const reconcileQ = useShopInventoryReconcileQuery(shopId, {
    branchId,
    shopProductSkuId,
    onlyMismatch: false,
    limit: 20,
  });

  const reconcileRow = reconcileQ.data?.data?.[0] ?? null;

  const movementsQ = useShopInventoryMovementsQuery(
    shopId,
    {
      branchId,
      shopProductSkuId,
      limit: SKU_DETAIL_TABLE_PAGE,
      offset: movementsOffset,
    },
    { enabled: !!branchId && !!shopProductSkuId },
  );

  const movementRows = movementsQ.data?.data ?? [];
  const lotsTotal = lotsQ.data?.meta.total ?? 0;
  const serialsTotal = serialsQ.data?.meta.total ?? 0;
  const movementsTotal = movementsQ.data?.meta.total ?? 0;

  const numberFmt = useMemo(() => new Intl.NumberFormat("lo-LA"), []);

  const onHand = row?.quantityOnHand;
  const reserved = row?.quantityReserved;
  const available =
    onHand != null && reserved != null ? onHand - reserved : null;

  const pageLoading =
    !!shopId &&
    !!branchId &&
    !!shopProductSkuId &&
    (stockQ.isLoading || stockQ.isFetching);

  const missingParams = !shopId || !branchId || !shopProductSkuId;

  return (
    <RequireMerchantPermission permission={MerchantPermissions.inventory.read}>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.inventorySkuDetail(shopId)} />
      </Header>

      <Main>
        {/* <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-fit gap-2 self-start"
            onClick={() => {
              if (!shopId) return;
              void nav({ to: INVENTORY_ROUTE, params: { shopId } });
            }}
          >
            <ArrowLeftIcon className="size-4" />
            ກັບໄປສາງ
          </Button>
        </div> */}

        {missingParams ? (
          <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
            <p className="font-medium text-card-foreground">ບໍ່ມີລະຫັດສາຂາ ຫຼື SKU</p>
            <p className="mt-2 text-muted-foreground text-sm">
              ເປີດຈາກຕາຕະລາງສາງໂດຍເລືອກ «ລາຍລະອຽດ» ຕໍ່ແຖວທີ່ມີສາຂາ
            </p>
          </div>
        ) : pageLoading ? (
          <div className="flex justify-center py-24">
            <Loader />
          </div>
        ) : stockQ.isError ? (
          <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-6">
            <p className="text-destructive text-sm">
              {stockQ.error instanceof Error
                ? stockQ.error.message
                : "ໂຫຼດລົ້ມເຫຼວ"}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-4"
              onClick={() => void stockQ.refetch()}
            >
              ລອງໃໝ່
            </Button>
          </div>
        ) : !row ? (
          <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
            <p className="font-medium text-card-foreground">
              ບໍ່ພົບຍອດສະຕ໋ອກຕາມສາຂານີ້
            </p>
            <p className="mt-2 text-muted-foreground text-sm">
              ອາດຍັງບໍ່ມີແຖວ inventories — ຮັບເຂົ້າສິນຄ້ານີ້ກ່ອນ
            </p>
            {canReceive && shopId && branchId && shopProductSkuId ? (
              <Button
                type="button"
                className="mt-4 gap-2"
                onClick={() => {
                  void nav({
                    to: INVENTORY_RECEIVE_ROUTE,
                    params: { shopId },
                    search: { branchId, shopProductSkuId },
                  });
                }}
              >
                <ArrowDownToLine className="size-4" />
                ໄປຮັບເຂົ້າ
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="space-y-8">
            <div
              className={cn(
                "rounded-xl border border-border bg-card p-5 shadow-sm",
                "flex flex-col gap-4 sm:flex-row sm:items-start",
              )}
            >
              <div className="flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted">
                {(() => {
                  const src = row.imageUrl
                    ? resolveImageSrc(row.imageUrl)
                    : null;
                  return src ? (
                    <img src={src} alt="" className="size-full object-cover" />
                  ) : (
                    <ImageIcon
                      className="size-9 text-muted-foreground"
                      aria-hidden
                    />
                  );
                })()}
              </div>
              <div className="min-w-0 flex-1 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="font-bold font-heading text-foreground text-xl tracking-tight">
                    {row.productName}
                  </h1>
                  <Badge variant="secondary" className="font-mono text-[10px]">
                    {trackingDisplayLabel(row.trackingMode)}
                  </Badge>
                </div>
                <p className="text-muted-foreground text-sm">
                  <span className="font-medium text-foreground">SKU:</span>{" "}
                  <span className="font-mono">{row.shopSku}</span>
                  {row.barcode ? (
                    <>
                      {" "}
                      ·{" "}
                      <span className="font-medium text-foreground">
                        Barcode:
                      </span>{" "}
                      <span className="font-mono">{row.barcode}</span>
                    </>
                  ) : null}
                </p>
                <p className="text-muted-foreground text-sm">
                  <span className="font-medium text-foreground">ສາຂາ:</span>{" "}
                  {row.branchName ?? "—"}
                </p>
                {row.inventoryUpdatedAt ? (
                  <p className="text-muted-foreground text-xs">
                    ອັບເດດສະຕ໋ອກລ່າສຸດ:{" "}
                    {formatDateTimeLocal(row.inventoryUpdatedAt)}
                  </p>
                ) : null}
              </div>
              <div className="flex shrink-0 flex-wrap gap-2 sm:flex-col">
                {canReceive && shopId && branchId ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => {
                      void nav({
                        to: INVENTORY_RECEIVE_ROUTE,
                        params: { shopId },
                        search: {
                          branchId,
                          shopProductSkuId: row.shopProductSkuId,
                        },
                      });
                    }}
                  >
                    <ArrowDownToLine className="size-4" />
                    ຮັບເຂົ້າ
                  </Button>
                ) : null}
                {shopId ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => {
                      void nav({
                        to: INVENTORY_MOVEMENTS_ROUTE,
                        params: { shopId },
                        search: {
                          offset: 0,
                          limit: 20,
                          branchId,
                          shopProductSkuId: row.shopProductSkuId,
                          type: undefined,
                          createdFrom: undefined,
                          createdTo: undefined,
                          recOnlyMismatch: true,
                        },
                      });
                    }}
                  >
                    <History className="size-4" />
                    ປະຫວັດທັງໝົດ
                  </Button>
                ) : null}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <SummaryCard
                title="ຄົງເຫຼືອ"
                value={onHand != null ? numberFmt.format(onHand) : "—"}
                description={stockSumDesc.onHand}
                icon={<PackageIcon className="size-5" aria-hidden />}
              />
              <SummaryCard
                title="ຈອງ"
                value={reserved != null ? numberFmt.format(reserved) : "—"}
                description={stockSumDesc.reserved}
                icon={<Lock className="size-5" aria-hidden />}
              />
              <SummaryCard
                title="ພ້ອມໃຊ້"
                value={available != null ? numberFmt.format(available) : "—"}
                description={stockSumDesc.available}
                icon={<CheckCircle2 className="size-5" aria-hidden />}
              />
            </div>

            {warehouseLocationsOn && branchId && shopProductSkuId ? (
              <ShopInventoryLocationDistributionPanel
                shopId={shopId!}
                branchId={branchId}
                shopProductSkuId={shopProductSkuId}
                trackingMode={tm}
                canAdjust={canAdjust}
              />
            ) : null}

            {reconcileRow?.mismatch ? (
              <div className="flex gap-3 rounded-xl border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-sm">
                <AlertTriangle
                  className="size-5 shrink-0 text-amber-600 dark:text-amber-400"
                  aria-hidden
                />
                <div>
                  <p className="font-semibold text-amber-950 dark:text-amber-100">
                    {reconcileLabels.title}
                  </p>
                  <p className="mt-1 text-amber-950/90 dark:text-amber-50/90">
                    on_hand = {numberFmt.format(reconcileRow.onHand)} ·{" "}
                    {reconcileLabels.derivedLabel} ={" "}
                    {numberFmt.format(reconcileRow.derivedQty)} — ກວດ ຮັບເຂົ້າ ຫຼື
                    ປະຫວັດສະຕ໋ອກ
                  </p>
                </div>
              </div>
            ) : null}

            {skuUsesLotTable(tm) ? (
              <section>
                <h2 className="mb-3 font-heading font-semibold text-foreground text-lg">
                  Lot
                </h2>
                {lotsQ.isLoading ? (
                  <Loader />
                ) : lotsQ.isError ? (
                  <p className="text-destructive text-sm">
                    {lotsQ.error instanceof Error
                      ? lotsQ.error.message
                      : "ໂຫຼດ lot ລົ້ມເຫຼວ"}
                  </p>
                ) : lotsTotal === 0 ? (
                  <p className="text-muted-foreground text-sm">
                    ບໍ່ມີ lot ໃນສາຂານີ້
                  </p>
                ) : (
                  <div className="overflow-hidden rounded-xl border border-border">
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[520px] text-sm">
                        <thead className="border-border border-b bg-muted/40">
                          <tr>
                            <th className="px-3 py-2 text-start font-medium">
                              Lot
                            </th>
                            <th className="px-3 py-2 text-end font-medium">
                              ຈຳນວນ
                            </th>
                            <th className="px-3 py-2 text-start font-medium">
                              ໝົດອາຍຸ
                            </th>
                            <th className="px-3 py-2 text-start font-medium">
                              ສະແກນ
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {(lotsQ.data?.items ?? []).map((lot) => (
                            <tr
                              key={lot.id}
                              className="border-border border-b last:border-0"
                            >
                              <td className="px-3 py-2 font-mono">
                                {lot.lotNo}
                              </td>
                              <td className="px-3 py-2 text-end tabular-nums">
                                {numberFmt.format(lot.qty)}
                              </td>
                              <td className="px-3 py-2 text-muted-foreground">
                                {lot.expiryDate ?? "—"}
                              </td>
                              <td className="px-3 py-2 font-mono text-muted-foreground text-xs">
                                {lot.lotScanCode ?? "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <InventorySubtablePager
                      offset={lotsOffset}
                      limit={SKU_DETAIL_TABLE_PAGE}
                      total={lotsTotal}
                      numberFmt={numberFmt}
                      disabled={lotsQ.isFetching}
                      onPrev={() =>
                        setLotsOffset((o) =>
                          Math.max(0, o - SKU_DETAIL_TABLE_PAGE),
                        )
                      }
                      onNext={() =>
                        setLotsOffset((o) => o + SKU_DETAIL_TABLE_PAGE)
                      }
                    />
                  </div>
                )}
              </section>
            ) : null}

            {skuUsesSerialTable(tm) ? (
              <section>
                <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <h2 className="font-heading font-semibold text-foreground text-lg">
                    Serial (in_stock)
                  </h2>
                  {shopId && branchId && shopProductSkuId ? (
                    <div className="flex w-full min-w-0 flex-col gap-1 text-sm sm:w-auto sm:min-w-[280px]">
                      <span className="text-muted-foreground">
                        ກອງຕາມ lot (ມີ serial ຄ້າງສະຕ໋ອກເທົ່ານັ້ນ)
                      </span>
                      <InfiniteCombobox<ShopInventoryLotWithSerialsLookupItem>
                        queryKey={[
                          "shops",
                          shopId,
                          "inventory",
                          "lots-with-serials-lookup",
                          branchId,
                          shopProductSkuId,
                        ]}
                        queryFn={async ({ search: q, pageParam }) => {
                          return shopInventoryApi.lookupLotsWithSerialsForSku(
                            shopId,
                            {
                              branchId,
                              shopProductSkuId,
                              page: pageParam,
                              search: q.trim() || undefined,
                              limit: 20,
                            },
                          );
                        }}
                        preloadQueryFn={(lotId) =>
                          shopInventoryApi.getLotForSerialFilter(
                            shopId,
                            lotId,
                            {
                              branchId,
                              shopProductSkuId,
                            },
                          )
                        }
                        getLabel={(item) =>
                          `${item.lotNo} (${numberFmt.format(item.serialCount)} serial)`
                        }
                        getValue={(item) => item.id}
                        value={serialLotId}
                        onValueChange={(val) => {
                          setSerialLotId(val);
                          setSerialsOffset(0);
                        }}
                        placeholder="ທັງໝົດ (ບໍ່ກອງ lot)"
                        className="h-9"
                        clearable
                      />
                    </div>
                  ) : null}
                </div>
                {serialsQ.isLoading ? (
                  <Loader />
                ) : serialsQ.isError ? (
                  <p className="text-destructive text-sm">
                    {serialsQ.error instanceof Error
                      ? serialsQ.error.message
                      : "ໂຫຼດ serial ລົ້ມເຫຼວ"}
                  </p>
                ) : serialsTotal === 0 ? (
                  <div className="rounded-xl border border-border bg-muted/20 px-4 py-3 text-sm">
                    <p className="text-muted-foreground">
                      {serialLotFilter ? (
                        <>ບໍ່ມີ serial ໃນ lot ທີ່ເລືອກ — ລອງເລືອກ lot ອື່ນ ຫຼື ທັງໝົດ</>
                      ) : tm === "lot_serial" ? (
                        <>
                          ບໍ່ມີ serial ພ້ອມຂາຍ — ຖ້າມີຍອດລວມແຕ່ບໍ່ມີແຖວ serial
                          ອາດເຄີຍປັບຍອດມື; ຮັບເຂົ້າ serial ຕາມ lot
                        </>
                      ) : (
                        <>
                          ບໍ່ມີ serial ພ້ອມຂາຍ — ຖ້າມີຍອດລວມແຕ່ບໍ່ມີແຖວ serial
                          ອາດເຄີຍປັບຍອດມື; ຮັບເຂົ້າ serial ເຂົ້າສະຕ໋ອກ
                        </>
                      )}
                    </p>
                    {canReceive && shopId && branchId ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="mt-3 gap-2"
                        onClick={() => {
                          void nav({
                            to: INVENTORY_RECEIVE_ROUTE,
                            params: { shopId },
                            search: {
                              branchId,
                              shopProductSkuId: row.shopProductSkuId,
                            },
                          });
                        }}
                      >
                        <ArrowDownToLine className="size-4" />
                        ຮັບເຂົ້າ
                      </Button>
                    ) : null}
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-xl border border-border">
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[480px] text-sm">
                        <thead className="border-border border-b bg-muted/40">
                          <tr>
                            <th className="px-3 py-2 text-start font-medium">
                              Serial
                            </th>
                            <th className="px-3 py-2 text-start font-medium">
                              Lot
                            </th>
                            <th className="px-3 py-2 text-start font-medium">
                              ສະແກນຫນ່ວຍ
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {(serialsQ.data?.items ?? []).map((s) => (
                            <tr
                              key={s.id}
                              className="border-border border-b last:border-0"
                            >
                              <td className="px-3 py-2 font-mono">
                                {s.serialNo}
                              </td>
                              <td className="px-3 py-2 font-mono text-muted-foreground">
                                {s.lotNo ?? "—"}
                              </td>
                              <td className="px-3 py-2 font-mono text-muted-foreground text-xs">
                                {s.unitScanCode ?? "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <InventorySubtablePager
                      offset={serialsOffset}
                      limit={SKU_DETAIL_TABLE_PAGE}
                      total={serialsTotal}
                      numberFmt={numberFmt}
                      disabled={serialsQ.isFetching}
                      onPrev={() =>
                        setSerialsOffset((o) =>
                          Math.max(0, o - SKU_DETAIL_TABLE_PAGE),
                        )
                      }
                      onNext={() =>
                        setSerialsOffset((o) => o + SKU_DETAIL_TABLE_PAGE)
                      }
                    />
                  </div>
                )}
              </section>
            ) : null}

            <section>
              <h2 className="mb-3 font-heading font-semibold text-foreground text-lg">
                ການເຄື່ອນໄຫວຫຼ້າສຸດ
              </h2>
              {movementsQ.isLoading ? (
                <Loader />
              ) : movementsQ.isError ? (
                <p className="text-destructive text-sm">
                  {movementsQ.error instanceof Error
                    ? movementsQ.error.message
                    : "ໂຫຼດປະຫວັດລົ້ມເຫຼວ"}
                </p>
              ) : movementsTotal === 0 ? (
                <p className="text-muted-foreground text-sm">ບໍ່ມີປະຫວັດ</p>
              ) : (
                <div className="overflow-hidden rounded-xl border border-border">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[560px] text-sm">
                      <thead className="border-border border-b bg-muted/40">
                        <tr>
                          <th className="px-3 py-2 text-start font-medium">
                            ເວລາ
                          </th>
                          <th className="px-3 py-2 text-start font-medium">
                            ປະເພດ
                          </th>
                          <th className="px-3 py-2 text-end font-medium">
                            ຈຳນວນ
                          </th>
                          <th className="px-3 py-2 text-start font-medium">
                            ໝາຍເຫດ
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {movementRows.map((m) => (
                          <tr
                            key={m.id}
                            className="border-border border-b last:border-0"
                          >
                            <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                              {formatDateTimeLocal(m.createdAt)}
                            </td>
                            <td className="px-3 py-2">
                              {movementTypeBadge(m.type)}
                            </td>
                            <td className="px-3 py-2 text-end font-mono tabular-nums">
                              {m.qty > 0 ? "+" : ""}
                              {numberFmt.format(m.qty)}
                            </td>
                            <td className="max-w-[220px] truncate px-3 py-2 text-muted-foreground text-xs">
                              {m.note ?? "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <InventorySubtablePager
                    offset={movementsOffset}
                    limit={SKU_DETAIL_TABLE_PAGE}
                    total={movementsTotal}
                    numberFmt={numberFmt}
                    disabled={movementsQ.isFetching}
                    onPrev={() =>
                      setMovementsOffset((o) =>
                        Math.max(0, o - SKU_DETAIL_TABLE_PAGE),
                      )
                    }
                    onNext={() =>
                      setMovementsOffset((o) => o + SKU_DETAIL_TABLE_PAGE)
                    }
                  />
                </div>
              )}
              {/* {shopId ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="mt-3 gap-2"
                  onClick={() => {
                    void nav({
                      to: INVENTORY_MOVEMENTS_ROUTE,
                      params: { shopId },
                      search: {
                        offset: 0,
                        limit: 20,
                        branchId,
                        shopProductSkuId: row.shopProductSkuId,
                        type: undefined,
                        createdFrom: undefined,
                        createdTo: undefined,
                        recOnlyMismatch: true,
                      },
                    });
                  }}
                >
                  <PackageIcon className="size-4" />
                  ເບິ່ງປະຫວັດເຕັມ
                </Button>
              ) : null} */}
            </section>
          </div>
        )}
      </Main>
    </RequireMerchantPermission>
  );
}
